import React, { useState } from 'react';

/**
 * Phase 3: Enhanced Order Detail View with collapsible sections
 */
const EnhancedOrderDetailView = ({ order, onBack, formatDate, getStatusBadge }) => {
  const [expandedSections, setExpandedSections] = useState({
    orderInfo: true,
    productDetails: true,
    aiConfig: true,
    files: true,
    activityLog: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!order) return null;

  const orderData = order.order;

  // Collapsible Section Component
  const CollapsibleSection = ({ title, icon, sectionKey, children, badgeCount }) => (
    <div className="bg-white rounded-lg shadow mb-4">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          {badgeCount !== undefined && (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
              {badgeCount}
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${expandedSections[sectionKey] ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expandedSections[sectionKey] && (
        <div className="px-6 pb-6 border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );

  // Image Thumbnail Component
  const ImageThumbnail = ({ file }) => (
    <div className="relative group">
      <img
        src={file.thumbnail_url || file.file_path}
        alt={file.filename}
        className="w-24 h-24 object-cover rounded border border-gray-300 cursor-pointer hover:border-blue-500 transition-colors"
        onClick={() => window.open(file.file_path, '_blank')}
      />
      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded flex items-center justify-center">
        <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-semibold">
          View Full
        </span>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="text-blue-600 hover:text-blue-800 font-medium mb-4 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Orders
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Details</h1>
            <p className="text-gray-600">Complete information for AI product order</p>
          </div>
          <div>
            {getStatusBadge(orderData.status)}
          </div>
        </div>
      </div>

      {/* Order Information Section */}
      <CollapsibleSection title="Order Information" icon="📋" sectionKey="orderInfo">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Order ID</label>
            <p className="text-sm font-mono text-gray-900 bg-gray-50 px-3 py-2 rounded">{orderData.order_id}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Customer Email</label>
            <p className="text-sm text-gray-900">{orderData.user_email}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Created Date</label>
            <p className="text-sm text-gray-900">{formatDate(orderData.created_at)}</p>
          </div>
          
          {orderData.stl_generated_at && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">STL Generated</label>
              <p className="text-sm text-gray-900">{formatDate(orderData.stl_generated_at)}</p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Generation Attempts</label>
            <p className="text-sm text-gray-900">{orderData.generation_attempts || 1}</p>
          </div>
          
          {orderData.stl_job_id && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">STL Job ID</label>
              <p className="text-sm font-mono text-gray-900 bg-gray-50 px-3 py-2 rounded">{orderData.stl_job_id}</p>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Product Details Section */}
      <CollapsibleSection title="Product Details" icon="📦" sectionKey="productDetails">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Product Type</label>
            <p className="text-sm text-gray-900 capitalize">{orderData.product_details?.product_type?.replace('_', ' ') || 'N/A'}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Size</label>
            <p className="text-sm text-gray-900 font-semibold">{orderData.product_details?.size || 'N/A'}</p>
          </div>
          
          {orderData.product_details?.background_theme && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Background Theme</label>
              <p className="text-sm text-gray-900">{orderData.product_details.background_theme}</p>
            </div>
          )}
          
          {orderData.product_details?.custom_text && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-500 mb-1">Custom Text</label>
              <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded">{orderData.product_details.custom_text}</p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Price</label>
            <p className="text-lg font-bold text-green-600">${orderData.total_price?.toFixed(2) || '69.00'}</p>
          </div>
        </div>
      </CollapsibleSection>

      {/* AI Configuration Section - Phase 3 */}
      {orderData.product_details?.ai_configuration && (
        <CollapsibleSection title="AI Configuration" icon="🤖" sectionKey="aiConfig">
          <div className="space-y-4 mt-4">
            {orderData.product_details.ai_configuration.model_used && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">AI Model Used</label>
                <p className="text-sm text-gray-900 font-mono bg-blue-50 px-3 py-2 rounded inline-block">
                  {orderData.product_details.ai_configuration.model_used}
                </p>
              </div>
            )}
            
            {orderData.product_details.ai_configuration.theme_selected !== null && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Theme Selected</label>
                <p className="text-sm text-gray-900">
                  {orderData.product_details.ai_configuration.theme_selected ? 'Moon Background' : 'Custom Theme'}
                </p>
              </div>
            )}
            
            {orderData.product_details.ai_configuration.ai_prompts && orderData.product_details.ai_configuration.ai_prompts.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">AI Prompts Generated</label>
                <div className="space-y-2">
                  {orderData.product_details.ai_configuration.ai_prompts.map((prompt, idx) => (
                    <div key={idx} className="bg-purple-50 border border-purple-200 rounded p-3">
                      <p className="text-xs text-purple-600 font-semibold mb-1">{prompt.type}</p>
                      <p className="text-sm text-gray-900">{prompt.prompt}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {orderData.product_details.ai_configuration.model_parameters && Object.keys(orderData.product_details.ai_configuration.model_parameters).length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Model Parameters</label>
                <details className="bg-gray-50 border border-gray-200 rounded p-3">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                    View Parameters
                  </summary>
                  <pre className="mt-2 text-xs text-gray-800 overflow-auto">
                    {JSON.stringify(orderData.product_details.ai_configuration.model_parameters, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Files Section with Thumbnails - Phase 3 */}
      <CollapsibleSection 
        title="Files" 
        icon="📁" 
        sectionKey="files"
        badgeCount={
          (orderData.product_details?.uploaded_images?.length || 0) + 
          (orderData.product_details?.preview_image ? 1 : 0) + 
          (orderData.product_details?.stl_file ? 1 : 0)
        }
      >
        <div className="space-y-6 mt-4">
          {/* Uploaded Images with Thumbnails */}
          {orderData.product_details?.uploaded_images && orderData.product_details.uploaded_images.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Uploaded Images ({orderData.product_details.uploaded_images.length})</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {orderData.product_details.uploaded_images.map((file, index) => (
                  <div key={index} className="space-y-2">
                    <ImageThumbnail file={file} />
                    <p className="text-xs text-gray-600 truncate" title={file.filename}>{file.filename}</p>
                    <p className="text-xs text-gray-500">{(file.file_size_bytes / 1024 / 1024).toFixed(2)} MB</p>
                    <a
                      href={file.file_path}
                      download={file.filename}
                      className="block w-full text-center px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                    >
                      📥 Download
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Preview Image */}
          {orderData.product_details?.preview_image && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Preview Image</h3>
              <div className="flex items-start gap-4 p-4 bg-blue-50 rounded border border-blue-200">
                <ImageThumbnail file={orderData.product_details.preview_image} />
                <div className="flex-1">
                  <p className="font-medium text-sm text-gray-900">{orderData.product_details.preview_image.filename}</p>
                  <p className="text-xs text-gray-500 mt-1">{(orderData.product_details.preview_image.file_size_bytes / 1024 / 1024).toFixed(2)} MB</p>
                  <a
                    href={orderData.product_details.preview_image.file_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    📥 Download
                  </a>
                </div>
              </div>
            </div>
          )}
          
          {/* STL File */}
          {orderData.product_details?.stl_file ? (
            <div>
              <h3 className="text-lg font-semibold mb-3">Generated STL File ✅</h3>
              <div className="p-4 bg-green-50 rounded border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm text-gray-900">{orderData.product_details.stl_file.filename}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {(orderData.product_details.stl_file.file_size_bytes / 1024 / 1024).toFixed(2)} MB • 
                      Generated: {formatDate(orderData.product_details.stl_file.uploaded_at)}
                    </p>
                  </div>
                  <a
                    href={orderData.product_details.stl_file.file_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded hover:bg-green-700"
                  >
                    📥 Download STL
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-yellow-800">⚠️ STL file not yet generated for this order</p>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Activity Log Section - Phase 3 */}
      {orderData.product_details?.activity_log && orderData.product_details.activity_log.length > 0 && (
        <CollapsibleSection 
          title="Activity Log" 
          icon="📝" 
          sectionKey="activityLog"
          badgeCount={orderData.product_details.activity_log.length}
        >
          <div className="mt-4">
            <div className="flow-root">
              <ul className="-mb-8">
                {orderData.product_details.activity_log.map((activity, idx) => (
                  <li key={idx}>
                    <div className="relative pb-8">
                      {idx !== orderData.product_details.activity_log.length - 1 && (
                        <span
                          className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                          aria-hidden="true"
                        />
                      )}
                      <div className="relative flex items-start space-x-3">
                        <div>
                          <div className={`relative px-1 flex h-8 w-8 items-center justify-center rounded-full ${
                            activity.event_type === 'generation_complete' 
                              ? 'bg-green-500'
                              : activity.event_type === 'generation_failed'
                              ? 'bg-red-500'
                              : activity.user_action
                              ? 'bg-blue-500'
                              : 'bg-gray-400'
                          }`}>
                            <span className="text-white text-sm">
                              {activity.event_type === 'generation_complete' ? '✓' :
                               activity.event_type === 'generation_failed' ? '✗' :
                               activity.user_action ? '👤' : '⚙'}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div>
                            <div className="text-sm">
                              <span className="font-medium text-gray-900">{activity.description}</span>
                            </div>
                            <p className="mt-0.5 text-xs text-gray-500">
                              {formatDate(activity.timestamp)}
                              {activity.user_action ? ' • User Action' : ' • System Event'}
                            </p>
                          </div>
                          {activity.details && Object.keys(activity.details).length > 0 && (
                            <div className="mt-2 text-xs text-gray-600 bg-gray-50 rounded p-2">
                              {Object.entries(activity.details).map(([key, value]) => (
                                <div key={key} className="flex gap-2">
                                  <span className="font-semibold">{key}:</span>
                                  <span>{typeof value === 'object' ? JSON.stringify(value) : value}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
};

export default EnhancedOrderDetailView;
